import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  CustomValidators,
  ErroMessages,
  IErroMessages,
  IRoute,
  TokenService
} from 'viggo-sistemas-core-web';

import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MarcaService } from '@app/modules/marca/services/marca.service';
import { IModeloTransporte } from '@app/modules/modelo_transporte/model/modelo_transporte.model';
import { ModeloTransporteService } from '@app/modules/modelo_transporte/services/modelo_transporte.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogData
} from '@app/shared/components';
import { Utils } from '@app/shared/helpers/utils';
import {
  validarAdicaoRaiz,
  verificarRotasDeAcessoRaiz
} from '@app/shared/utils';
import { Dialog } from 'primeng/dialog';

@Component({
  templateUrl: './fast-modelo-transporte.component.html',
  styleUrls: ['./fast-modelo-transporte.component.scss'],
  providers: [ModeloTransporteService, MarcaService]
})
export class FastModeloTransporteComponent implements OnInit, AfterViewInit {
  @ViewChild('fastModeloTransp') fastModeloTransp: Dialog;
  public form: UntypedFormGroup;

  loading = false;

  displayMessage = {
    nome: {
      duplicate: 'Já existe um modelo de transporte com esse nome!',
      required: 'Nome é obrigatório',
      maxlength: 'Não pode ter mais de 60 caracteres',
      isEmptyValue: 'Valor inválido: remova os campos em branco!'
    },
    km_diferencial: {
      required: 'KM diferencial é obrigatório',
      max_value: 'Inválido: passou dos limite',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      notNumbers: 'Inválido: insira um número',
      invalidNumber: 'Valor inválido'
    },
    km_motor: {
      required: 'KM do motor é obrigatório',
      max_value: 'Inválido: passou dos limite',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      notNumbers: 'Inválido: insira um número',
      invalidNumber: 'Valor inválido'
    },
    km_caixa: {
      required: 'KM da caixa é obrigatório',
      max_value: 'Inválido: passou dos limite',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      notNumbers: 'Inválido: insira um número',
      invalidNumber: 'Valor inválido'
    },
    qtd_pneus: {
      required: 'Quantidade de pneus é obrigatório',
      min: 'Valor deve ser maior que 0',
      max_value: 'Inválido: passou dos limite',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      notNumbers: 'Inválido: insira um número',
      invalidNumber: 'Valor inválido'
    },
    qtd_eixos: {
      required: 'Quantidade de eixos é obrigatório',
      min: 'Valor deve ser maior que 0',
      max_value: 'Inválido: passou dos limite',
      before_split: 'Inválido: erro na parte inteira',
      after_split: 'Inválido: erro na parte decimal',
      errorComma: 'Inválido: excesso de virgulas',
      errorDots: 'Inválido: excesso de pontos',
      notNumbers: 'Inválido: insira um número',
      invalidNumber: 'Valor inválido'
    }
  };

  constructor(
    public dialogRef: MatDialogRef<FastModeloTransporteComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IModeloTransporte,
    protected fb: UntypedFormBuilder,
    public entityService: ModeloTransporteService,
    public marcaService: MarcaService,
    public tokenService: TokenService,
    public matSnackBar: MatSnackBar,
    public matDialog: MatDialog,
    public router: Router
  ) {}

  ngAfterViewInit(): void {
    this.fastModeloTransp.maximize();
  }

  ngOnInit() {
    this.form = this.fb.group({
      nome: [
        '',
        [
          Validators.required,
          Validators.maxLength(60),
          CustomValidators.isEmptyValue()
        ]
      ],
      marca: [
        null,
        [
          Validators.required,
          CustomValidators.typeIs('object'),
          CustomValidators.isEmptyValue()
        ]
      ],
      km_caixa: [0, [CustomValidators.validateCurrencyValues(17, 4)]],
      km_diferencial: [0, [CustomValidators.validateCurrencyValues(17, 4)]],
      km_motor: [0, [CustomValidators.validateCurrencyValues(17, 4)]],
      qtd_pneus: [0, [CustomValidators.validateCurrencyValues(17, 4)]],
      qtd_eixos: [0, [CustomValidators.validateCurrencyValues(17, 4)]]
    });
    this.validateForm();
  }

  onSubmit() {
    if (this.form.valid) {
      this.loading = true;
      const { ...rest } = this.form.value;
      let entity = {
        domain_id: this.tokenService.getDomain().id,
        ...rest,
        qtd_eixos: Utils.replaceCommaToDot(this.form.value.qtd_eixos),
        qtd_pneus: Utils.replaceCommaToDot(this.form.value.qtd_pneus),
        km_caixa: Utils.replaceCommaToDot(this.form.value.km_caixa),
        km_diferencial: Utils.replaceCommaToDot(this.form.value.km_diferencial),
        km_motor: Utils.replaceCommaToDot(this.form.value.km_motor),
        marca_id: this.form.value.marca.id || null
      };
      entity = Utils.onlyAttributes(
        Utils.clearObject(Utils.trimAttributes(entity)),
        [
          'domain_id',
          'marca_id',
          'nome',
          'codigo',
          'id',
          'km_motor',
          'km_diferencial',
          'km_caixa',
          'qtd_pneus',
          'qtd_eixos'
        ]
      );
      this.entityService.insert(entity).subscribe(
        servico => {
          if (servico) {
            this.matSnackBar.open(
              `Novo modelo de transporte cadastrado com sucesso.`,
              'X',
              {
                duration: 2000,
                horizontalPosition: 'right',
                verticalPosition: 'top',
                panelClass: ['msg-success']
              }
            );
            this.dialogRef.close(servico);
          }
        },
        (err: HttpErrorResponse) => {
          this.displayError(err);
          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
    }
  }

  validateOnlyNumbers(event) {
    if (
      !/^\d+$/.test(event.key) &&
      !event.ctrlKey &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return false;
  }

  validateNumbers(event) {
    if (
      !/^\d+$/.test(event.key) &&
      !/[`,.]/.test(event.key) &&
      !event.ctrlKey &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight'
    )
      return false;
  }

  markAllControlsAsDirty() {
    Object.keys(this.form.controls).forEach(controlKey => {
      controlKey = String(controlKey);
      const control = this.form.get(controlKey);
      if (!control || control.valid) return;
      control.markAsDirty();
    });
  }

  validateForm() {
    this.form.markAllAsTouched();
    this.markAllControlsAsDirty();
  }

  /**
   * Verifica se preenche ou nao o menu de user
   * @returns
   */
  checkDisponibilidade(action: string, params = '', url: string) {
    const routesAuthorization = verificarRotasDeAcessoRaiz(
      this.tokenService.getRoutes(),
      '/marcas'
    ) as IRoute[];

    const url_return = validarAdicaoRaiz(
      routesAuthorization,
      action,
      params,
      '/marcas'
    );
    // eslint-disable-next-line no-unneeded-ternary
    return url_return ? true : false;
  }

  actionFnCheckEntityExist(control: AbstractControl) {
    Utils.checkIfValueExist(
      control,
      this.entityService,
      'nome',
      this.tokenService.getDomain().id
    );
  }

  displayError(err: HttpErrorResponse, showDefaultMsg = false) {
    const errorMsg = new ErroMessages({
      route: this.router.url,
      router: this.router,
      matSnackBar: this.matSnackBar,
      httpError: err,
      showDefaultMsg: showDefaultMsg,
      loginService: null,
      tokenService: this.tokenService,
      dialogComponent: ConfirmDialogComponent,
      matDialog: this.matDialog,
      dialogData: {
        config: {
          data: {
            title: 'Aviso - Erro ao realizar operação',
            bodyMessage: err.error,
            icon: 'warning',
            icon_color: 'accent',
            singleBtn: true
          } as ConfirmDialogData,
          disableClose: true,
          width: '500px'
        } as MatDialogConfig<any>
      }
    } as IErroMessages);
    errorMsg.showMessageObject(err.error);
    // errorMsg.checkError();
  }
}
